import React, { useState } from "react";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import { getEnvVar } from "../../config/RuntimeConfig";

const ReCaptchaButton = ({
                             title,
                             id,
                             action,
                             children,
                             buttonHandler,
                             cssClass,
                             disabled,
                             readOnly,
                             style,
                         }: {
    title: string;
    action?: string;
    id?: string;
    children: string | React.ReactNode;
    buttonHandler?: (token?: string) => void;
    cssClass?: string;
    disabled?: boolean;
    readOnly?: boolean;
    style?: React.CSSProperties;
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loading, setLoading] = useState(false);
  const isReadOnly = !!readOnly;
  const isRecaptchaDisabled = getEnvVar('DISABLED_RECAPTCHA') === "true";

  const tryExecuteRecaptcha = async (retries = 5, delay = 1000): Promise<string | null> => {
    for (let attempt = 0; attempt < retries; attempt++) {
      if (executeRecaptcha) {
        try {
          return await executeRecaptcha(action ?? "submit");
        } catch (err) {
          console.error("executeRecaptcha failed:", err);
        }
      }

      console.warn("executeRecaptcha not ready, retrying...");
      await new Promise((res) => setTimeout(res, delay));
    }

    console.error("executeRecaptcha is still not ready after retries");
    return null;
  };

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isReadOnly || disabled) {
      return;
    }

    // Support automated tests in lower environments (and local dev) where
    // loading Google's script can be flaky/unavailable.
    if (isRecaptchaDisabled) {
      if (buttonHandler) {
        buttonHandler(undefined);
      }
      return;
    }

    setLoading(true);

    const token = await tryExecuteRecaptcha();
    if (buttonHandler) {
      buttonHandler(token ?? undefined);
    }

    setLoading(false);
  };

  const isButtonDisabled = (disabled || loading) && !isReadOnly;

  return (
    <button
      id={id ?? ''}
      title={title}
      className={cssClass ? cssClass : "ontario-button ontario-button--primary recaptcha-button"}
      onClick={handleClick}
      aria-disabled={isReadOnly || disabled || loading}
      disabled={isButtonDisabled}
      style={style}
    >
      {loading ? "Verifying..." : children}
    </button>
  );
};

export default ReCaptchaButton;
